import { PlusOutlined } from "@ant-design/icons";
import { useSearch } from "@tanstack/react-router";
import { Alert, Button, Card, Empty, Modal, Spin, Typography } from "antd";
import {
    useDestroyFormFieldMutation,
    useGetFormFieldUsagesQuery,
    useGetFormFieldsQuery,
    useStoreFormFieldMutation,
    useUpdateFormFieldMutation,
} from "api/TenantSlug/FormField/hooks";
import { LocalFieldSettingsDrawer } from "components/Drawers/LocalFieldSettingsDrawer/LocalFieldSettingsDrawer";
import { FormFieldEditorHeader } from "components/Forms/FormFieldEditorFields/FormFieldEditorHeader";
import { formSchema, FormStructureFormData } from "components/Forms/FormFieldsForm/formSchema";
import { SortableFormFieldList } from "components/Forms/FormFieldSortable/SortableFormFieldList";
import { SortableFormFieldRow } from "components/Forms/FormFieldSortable/SortableFormFieldRow";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { TenantLink } from "components/UI/TenantLink/TenantLink";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useEffect, useState } from "react";
import { useFieldArray, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { FormFieldTypes } from "types/enums";
import { DrawerId, FormId } from "types/models";

import { LocalFieldsEditorItem } from "./LocalFieldsEditorItem";

import FormFieldData = App.Data.FormFieldData;
import StoreFormFieldRequest = App.Http.Requests.FormField.StoreFormFieldRequest;
import UpdateFormFieldRequest = App.Http.Requests.FormField.UpdateFormFieldRequest;

interface SearchParams {
    [`localFieldSettings-formFieldId`]?: string;
}

const mapFormFieldToFormState = (field: FormFieldData) => ({
    ...field,
    id: field.id ?? undefined,
    formFieldSelectItems: field.formFieldSelectItems || [],
    formFieldRadioValues: field.formFieldRadioValues || [],
    validation_scenarios:
        field.validationScenarios?.map((scenario, index) => ({ id: scenario.id, order: index })) || [],
    modification_scenarios:
        field.modificationScenarios?.map((scenario, index) => ({ id: scenario.id, order: index })) || [],
    autocomplete_scenarios:
        field.autocompleteScenarios?.map((scenario, index) => ({ id: scenario.id, order: index })) || [],
});

const createEmptyLocalField = () => ({
    system_name: "",
    visible_description: "",
    type: FormFieldTypes.Text,
    is_required: false,
    is_hidden: false,
    is_global: true,
    min: null,
    max: null,
    default_value: null,
    doc_note: null,
    page: null,
    example_value: null,
    not_show_if_system_name_equals: null,
    show_only_if_system_name_equals: null,
    validation_regex: null,
    frontend_format: null,
    placeholder: null,
    form_structure_id: null,
    parent_form_field_id: null,
    validation_sequence_step_id: null,
    data_modification_sequence_step_id: null,
    autocomplete_sequence_step_id: null,
    validation_scenarios: [],
    modification_scenarios: [],
    autocomplete_scenarios: [],
    data_modification_accessor: null,
    autocomplete_accessor: null,
    formFieldSelectItems: [],
    formFieldRadioValues: [],
});

const toStoreRequest = (
    field: NonNullable<FormStructureFormData["formFields"]>[number],
    weight: number
): StoreFormFieldRequest => ({
    ...field,
    is_global: true,
    form_structure_id: null,
    parent_form_field_id: null,
    weight,
    type: field.type ?? FormFieldTypes.Text,
    is_required: field.is_required ?? false,
    is_hidden: field.is_hidden ?? false,
    validation_sequence_step_id: field.validation_sequence_step_id ?? null,
    data_modification_sequence_step_id: field.data_modification_sequence_step_id ?? null,
    autocomplete_sequence_step_id: field.autocomplete_sequence_step_id ?? null,
    data_modification_accessor: field.data_modification_accessor ?? null,
    autocomplete_accessor: field.autocomplete_accessor ?? null,
    validation_scenarios: field.validation_scenarios || [],
    modification_scenarios: field.modification_scenarios || [],
    autocomplete_scenarios: field.autocomplete_scenarios || [],
    formFieldSelectItems: field.formFieldSelectItems || [],
    formFieldRadioValues: field.formFieldRadioValues || [],
});

interface LocalFieldsEditorProps {
    formFields: FormFieldData[];
    isRefetching: boolean;
    deletedIds: string[];
    setDeletedIds: React.Dispatch<React.SetStateAction<string[]>>;
}

const LocalFieldsEditor = ({ formFields, isRefetching, deletedIds, setDeletedIds }: LocalFieldsEditorProps) => {
    const { t } = usePaldockTranslation("forms.local-fields");
    const { control, reset } = useGeneralForm<FormStructureFormData>();
    const { fields, append, remove, move } = useFieldArray<FormStructureFormData>({ control, name: "formFields" });
    const watchedFields = useWatch({ control, name: "formFields" }) || [];
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const selectedFormFieldId = searchParams[`${DrawerId.LocalFieldSettings}-formFieldId`];
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const usagesQuery = useGetFormFieldUsagesQuery(pendingDeleteId ?? "", {
        enabled: Boolean(pendingDeleteId),
    });

    const confirmDelete = () => {
        if (!pendingDeleteId) return;

        setDeletedIds((ids) => (ids.includes(pendingDeleteId) ? ids : [...ids, pendingDeleteId]));
        setPendingDeleteId(null);
    };

    useEffect(() => {
        reset({ formFields: formFields.map(mapFormFieldToFormState) });
        setDeletedIds([]);
    }, [formFields, reset, setDeletedIds]);

    return (
        <>
            <Card
                title={t("title")}
                extra={
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => append(createEmptyLocalField())}>
                        {t("actions.add")}
                    </Button>
                }
            >
                <Spin spinning={isRefetching}>
                    {fields.length > 0 && (
                        <FormFieldEditorHeader
                            visibleDescriptionLabel={t("fields.visible_description.label")}
                            placeholderLabel={t("fields.placeholder.label")}
                        />
                    )}
                    <SortableFormFieldList itemIds={fields.map(({ id }) => id)} onMove={move}>
                        <div className="flex flex-col gap-4">
                            {fields.map((field, index) => (
                                <SortableFormFieldRow key={field.id} id={field.id}>
                                    <LocalFieldsEditorItem
                                        index={index}
                                        isDeleted={Boolean(
                                            watchedFields[index]?.id &&
                                                deletedIds.includes(String(watchedFields[index]?.id))
                                        )}
                                        onDelete={() => {
                                            const persistedId = watchedFields[index]?.id;
                                            if (persistedId) {
                                                setPendingDeleteId(persistedId);
                                                return;
                                            }
                                            remove(index);
                                        }}
                                    />
                                </SortableFormFieldRow>
                            ))}
                            {fields.length === 0 && <Empty description={t("empty")} />}
                        </div>
                    </SortableFormFieldList>
                </Spin>
            </Card>
            {selectedFormFieldId && <LocalFieldSettingsDrawer formFieldId={selectedFormFieldId} />}
            <Modal
                open={Boolean(pendingDeleteId)}
                title={t("delete_modal.title")}
                okText={t("delete_modal.confirm")}
                cancelText={t("actions.cancel")}
                okButtonProps={{ danger: true, disabled: usagesQuery.isError || usagesQuery.isFetching }}
                onOk={confirmDelete}
                onCancel={() => setPendingDeleteId(null)}
                destroyOnClose
            >
                <Typography.Paragraph>{t("delete_modal.description")}</Typography.Paragraph>
                {usagesQuery.isFetching && <Spin />}
                {usagesQuery.isError && <Alert type="error" showIcon message={t("delete_modal.usages_load_error")} />}
                {!usagesQuery.isFetching && !usagesQuery.isError && usagesQuery.data?.usages.length === 0 && (
                    <Typography.Paragraph type="secondary">{t("delete_modal.no_usages")}</Typography.Paragraph>
                )}
                {!usagesQuery.isFetching && !usagesQuery.isError && Boolean(usagesQuery.data?.usages.length) && (
                    <>
                        <Typography.Paragraph strong>{t("delete_modal.used_in")}</Typography.Paragraph>
                        <ul className="list-disc pl-5">
                            {usagesQuery.data?.usages.map((usage) => (
                                <li key={usage.form_field_id}>
                                    <TenantLink
                                        to={`/tool/structure/${usage.form_structure_type}/${usage.form_structure_id}/structure`}
                                        target="_blank"
                                    >
                                        {usage.form_structure_name || usage.form_structure_system_name}
                                    </TenantLink>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </Modal>
        </>
    );
};

export const LocalFieldsForm = () => {
    const { t } = usePaldockTranslation("forms.local-fields");
    const [deletedIds, setDeletedIds] = useState<string[]>([]);
    const storeMutation = useStoreFormFieldMutation();
    const updateMutation = useUpdateFormFieldMutation();
    const destroyMutation = useDestroyFormFieldMutation();
    const query = useGetFormFieldsQuery({
        filter: {
            include:
                "formFieldSelectItems,formFieldRadioValues,validationScenarios,modificationScenarios,autocompleteScenarios",
            filter: { is_global: true },
            sort: "weight",
        },
        page: { number: 1, size: 1000 },
    });

    const handleSubmit = async (values: FormStructureFormData) => {
        try {
            await Promise.all(deletedIds.map((id) => destroyMutation.mutateAsync(id)));
            await Promise.all(
                (values.formFields || [])
                    .filter((field) => !field.id || !deletedIds.includes(field.id))
                    .map((field, index) => {
                        const request = toStoreRequest(field, index);
                        if (!field.id) return storeMutation.mutateAsync(request);

                        return updateMutation.mutateAsync({
                            formFieldId: field.id,
                            request: { ...request, id: field.id } as UpdateFormFieldRequest,
                        });
                    })
            );
            toast.success(t("messages.save_success"));
            await query.refetch();
        } catch {
            toast.error(t("messages.save_error"));
        }
    };

    if (query.isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spin size="large" />
            </div>
        );
    }

    if (query.isError) return <Alert type="error" showIcon message={t("messages.load_error")} />;

    const formFields = query.data?.formFields || [];

    return (
        <GeneralFormProvider<FormStructureFormData>
            id={FormId.LocalFieldsForm}
            schema={formSchema}
            resetOnSubmit={false}
            defaultValues={{ formFields: formFields.map(mapFormFieldToFormState) }}
            onSubmit={handleSubmit}
        >
            <LocalFieldsEditor
                formFields={formFields}
                isRefetching={query.isFetching}
                deletedIds={deletedIds}
                setDeletedIds={setDeletedIds}
            />
        </GeneralFormProvider>
    );
};
